import { useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  BulkLeaveWaitlistCommandInput,
  BulkLeaveWaitlistCommandOutput,
  ShiftWaitlistEntryGroup,
} from '@amzn/red-velvet-api';
import { useFeatures, getEnabledFeatures } from '../features';
import { getClient } from '../../utils/getClient';

const MUTATION_PREFIX = ['RedVelvetApi', 'bulkLeaveWaitlist'];

// BulkLeaveWaitlist caps the total waitlistEntryIds per request at 100 (server MAX_BATCH). We chunk
// larger removals into sequential <=100 requests and aggregate the per-entry results, so callers can
// pass an arbitrary number of entries (e.g. clearing a full shift waitlist) without hitting a 400.
const MAX_BATCH = 100;

interface EntryPair {
  shiftId: string;
  waitlistEntryId: string;
}

/** Flattens the shift-grouped input into (shiftId, waitlistEntryId) pairs. */
function flattenEntries(input: BulkLeaveWaitlistCommandInput): EntryPair[] {
  const pairs: EntryPair[] = [];
  for (const group of input.entries ?? []) {
    for (const waitlistEntryId of group.waitlistEntryIds ?? []) {
      pairs.push({ shiftId: group.shiftId, waitlistEntryId });
    }
  }
  return pairs;
}

/** Re-groups a flat batch of pairs back into the shift-grouped request shape. */
function toEntries(pairs: EntryPair[]): ShiftWaitlistEntryGroup[] {
  const byShift = new Map<string, string[]>();
  for (const { shiftId, waitlistEntryId } of pairs) {
    const ids = byShift.get(shiftId) ?? [];
    ids.push(waitlistEntryId);
    byShift.set(shiftId, ids);
  }
  return Array.from(byShift.entries()).map(([shiftId, waitlistEntryIds]) => ({
    shiftId,
    waitlistEntryIds,
  }));
}

export function useBulkLeaveWaitlist() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));

  return useMutation<BulkLeaveWaitlistCommandOutput, Error, BulkLeaveWaitlistCommandInput>({
    mutationKey: MUTATION_PREFIX,
    mutationFn: async (input) => {
      const pairs = flattenEntries(input);
      // Fast path: a single request when within the server batch cap (preserves prior behavior).
      if (pairs.length <= MAX_BATCH) {
        return client.bulkLeaveWaitlist(input);
      }
      // Chunk sequentially and merge the per-entry outcomes into one aggregated result.
      const succeededDeletedWaitlistEntries: string[] = [];
      const failedDeletedWaitlistEntries: NonNullable<
        BulkLeaveWaitlistCommandOutput['failedDeletedWaitlistEntries']
      > = [];
      // $metadata is required on the command output; carry the most recent response's metadata.
      let metadata: BulkLeaveWaitlistCommandOutput['$metadata'] = {};
      for (let i = 0; i < pairs.length; i += MAX_BATCH) {
        const response = await client.bulkLeaveWaitlist({
          entries: toEntries(pairs.slice(i, i + MAX_BATCH)),
        });
        metadata = response.$metadata;
        succeededDeletedWaitlistEntries.push(...(response.succeededDeletedWaitlistEntries ?? []));
        failedDeletedWaitlistEntries.push(...(response.failedDeletedWaitlistEntries ?? []));
      }
      return { $metadata: metadata, succeededDeletedWaitlistEntries, failedDeletedWaitlistEntries };
    },
  });
}
